import React from 'react';

function MobileDoctorProfile({ user, onNavigate, onLogout }) {
  // Format name gracefully to prevent double "Dr." prefixes
  const rawName = user?.name || 'User';
  const displayName = rawName.toLowerCase().startsWith('dr.') ? rawName : `Dr. ${rawName}`;

  return (
    <div className="pb-8 bg-[#f8f9fe] min-h-full" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* TOP DOCTOR PROFILE CARD */}
      <section className="px-4 pt-3 pb-2">
        <div 
          className="rounded-3xl p-5 text-white relative overflow-hidden shadow-xl"
          style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 50%, #6366f1 100%)' }}
        >
          {/* Subtle Background Watermark Icon */}
          <div className="absolute -right-4 -bottom-4 opacity-15 pointer-events-none">
            <span className="material-symbols-outlined text-9xl">health_and_safety</span>
          </div>

          {/* Profile Header Info */}
          <div className="flex items-center gap-4 relative z-10 mb-6">
            <div className="w-16 h-16 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center font-black text-2xl shadow-inner shrink-0">
              {user?.name?.charAt(0) || 'D'}
            </div>
            <div className="min-w-0">
              <h2 className="font-extrabold text-lg text-white truncate leading-tight">
                {displayName}
              </h2>
              <p className="text-white/80 text-xs font-medium mt-0.5">
                {user?.specialization || 'Doctor'}
              </p>
              <p className="text-white/60 text-[11px] font-mono mt-0.5">
                NMC: {user?.nmcNumber || 'NMC-XXXX'}
              </p>
            </div>
          </div>

          {/* Status Badges */}
          <div className="grid grid-cols-3 gap-2 relative z-10">
            <div className="bg-white/15 backdrop-blur-md px-3 py-2 rounded-xl text-center">
              <p className="text-[10px] font-bold tracking-wider text-white/70 uppercase">Patients</p>
              <p className="text-xs font-black text-white mt-0.5">{user?.patientCount || 0}</p>
            </div>
            <div className="bg-white/15 backdrop-blur-md px-3 py-2 rounded-xl text-center flex flex-col items-center justify-center">
              <span className="material-symbols-outlined text-sm text-blue-200" style={{ fontVariationSettings: '"FILL" 1' }}>verified</span>
              <p className="text-[10px] font-extrabold tracking-wider text-white uppercase mt-0.5">Verified</p>
            </div>
            <div className="bg-emerald-500/30 backdrop-blur-md px-3 py-2 rounded-xl text-center flex flex-col items-center justify-center border border-emerald-400/30">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse mb-0.5"></span>
              <p className="text-[10px] font-extrabold tracking-wider text-emerald-200 uppercase">Active</p>
            </div>
          </div>
        </div>
      </section>

      {/* MENU CONTENT */}
      <section className="px-4 space-y-6 pt-3">

        {/* Account Section */}
        <div>
          <div className="flex items-center gap-2 mb-2 ml-1">
            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
              <span className="material-symbols-outlined text-sm">person</span>
            </div>
            <h3 className="text-xs font-black text-[#1e293b] tracking-wider uppercase">Account</h3>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-xs">
            {[
              {
                icon: 'person',
                iconBg: 'bg-blue-50 text-blue-600',
                label: 'Personal Information',
                desc: 'Update your personal details',
                action: () => onNavigate('personalInfo')
              },
              {
                icon: 'medical_services',
                iconBg: 'bg-purple-50 text-purple-600',
                label: 'Specialization & NMC',
                desc: 'View your specialization and NMC',
                action: () => onNavigate('specialization')
              },
            ].map((item, i) => (
              <button
                key={i}
                onClick={item.action}
                className="w-full flex items-center justify-between p-3.5 hover:bg-slate-50 active:bg-slate-100 transition-all border-b border-slate-50 last:border-b-0 text-left"
              >
                <div className="flex items-center gap-3.5">
                  <div className={`w-10 h-10 rounded-xl ${item.iconBg} flex items-center justify-center shrink-0`}>
                    <span className="material-symbols-outlined text-xl">{item.icon}</span>
                  </div>
                  <div>
                    <p className="font-bold text-sm text-[#0f172a]">{item.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{item.desc}</p>
                  </div>
                </div>
                <span className="material-symbols-outlined text-slate-300 text-lg">chevron_right</span>
              </button>
            ))}
          </div>
        </div>

        {/* Support Section */}
        <div>
          <div className="flex items-center gap-2 mb-2 ml-1">
            <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
              <span className="material-symbols-outlined text-sm">support_agent</span>
            </div>
            <h3 className="text-xs font-black text-[#1e293b] tracking-wider uppercase">Support</h3>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-xs">
            {[
              {
                icon: 'notifications',
                iconBg: 'bg-rose-50 text-rose-500',
                label: 'Notifications',
                desc: 'Manage your notifications',
                action: () => onNavigate('notifications')
              },
              {
                icon: 'settings',
                iconBg: 'bg-indigo-50 text-indigo-600',
                label: 'Settings',
                desc: 'Manage your app preferences',
                action: () => onNavigate('settings')
              },
              {
                icon: 'help',
                iconBg: 'bg-sky-50 text-sky-600',
                label: 'Help Center',
                desc: 'Get help and support',
                action: () => onNavigate('help')
              },
            ].map((item, i) => (
              <button
                key={i}
                onClick={item.action}
                className="w-full flex items-center justify-between p-3.5 hover:bg-slate-50 active:bg-slate-100 transition-all border-b border-slate-50 last:border-b-0 text-left"
              >
                <div className="flex items-center gap-3.5">
                  <div className={`w-10 h-10 rounded-xl ${item.iconBg} flex items-center justify-center shrink-0`}>
                    <span className="material-symbols-outlined text-xl">{item.icon}</span>
                  </div>
                  <div>
                    <p className="font-bold text-sm text-[#0f172a]">{item.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{item.desc}</p>
                  </div>
                </div>
                <span className="material-symbols-outlined text-slate-300 text-lg">chevron_right</span>
              </button>
            ))}
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={onLogout}
          className="w-full py-3.5 bg-red-50 hover:bg-red-100/80 active:scale-[0.98] border border-red-100 text-red-600 font-bold rounded-2xl flex items-center justify-center gap-2 transition-all shadow-xs text-sm"
        >
          <span className="material-symbols-outlined text-xl">logout</span>
          Logout Account
        </button>

        {/* Footer */}
        <footer className="pt-2 pb-6 text-center">
          <p className="text-slate-400 text-[11px] font-medium tracking-wide">
            My Health Journal v1.0.0 • 🇮🇳 Made in India
          </p>
        </footer>
      </section>
    </div>
  );
}

export default MobileDoctorProfile;